import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import { addReportToServer } from "../../functions/sendReport";
import { formatMmSs, parseTimeToSeconds } from "../../utils/yogaPoints";
import { readYogaSession, saveYogaSession } from "../../utils/yogaSession";
import ChooseFileButton from "../../components/yoga/ChooseFileButton";
import YogaHud from "../../components/yoga/YogaHud";
import {
  FROM_MOBILE_APP_STORAGE_KEY,
  isCoarseTouchDevice,
  isNativeAppWebView,
} from "../../utils/mobileApp";
import {
  YOGA_FILE_PICKED_EVENT,
  flutterPayloadToFile,
} from "../../utils/yogaFlutterFile";
import { getUploadCount, isUploadOpen } from "../../utils/yogaTheme";
import { uploadYogaMediaViaPresign } from "../../utils/yogaMediaUpload";
import {
  fetchYogaGameStatus,
  getYogaResumePath,
} from "../../utils/yogaResumeRoute";
import YogaThemedModal from "../../components/yoga/YogaThemedModal";
import "./yogaUpload.css";

const IMAGE_EXT = new Set(["jpg", "jpeg", "png"]);
const VIDEO_EXT = new Set(["mp4", "mov"]);
const IMAGE_MIME = new Set(["image/jpeg", "image/jpg", "image/png"]);
const VIDEO_MIME = new Set(["video/mp4", "video/quicktime"]);
const MAX_BYTES = 50 * 1024 * 1024;

function isValidFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  if (IMAGE_MIME.has(mime) || VIDEO_MIME.has(mime)) return true;
  const parts = file.name.split(".");
  if (parts.length < 2) return mime.startsWith("image/") || mime.startsWith("video/");
  const ext = parts.pop().toLowerCase();
  return IMAGE_EXT.has(ext) || VIDEO_EXT.has(ext);
}

function mediaPreviewType(file) {
  if (!file) return null;
  const mime = String(file.type || "").toLowerCase();
  if (mime.startsWith("video/") || VIDEO_MIME.has(mime)) return "video";
  if (mime.startsWith("image/") || IMAGE_MIME.has(mime)) return "image";
  const ext = file.name.split(".").pop().toLowerCase();
  return VIDEO_EXT.has(ext) ? "video" : "image";
}

function YogaSamples({ sampleImgUrl, sampleGifUrl, titleColor, className = "" }) {
  if (!sampleImgUrl && !sampleGifUrl) return null;

  return (
    <div className={`yu-samples${className ? ` ${className}` : ""}`}>
      <p className="yu-samples-title" style={{ color: titleColor }}>
        Example uploads
      </p>
      <div className="yu-samples-row">
        {sampleImgUrl ? (
          <figure className="yu-sample-item">
            <img src={sampleImgUrl} alt="Sample yoga photo" className="yu-sample-media" />
            <figcaption>Photo</figcaption>
          </figure>
        ) : null}
        {sampleGifUrl ? (
          <figure className="yu-sample-item">
            <img src={sampleGifUrl} alt="Sample yoga GIF" className="yu-sample-media" />
            <figcaption>GIF</figcaption>
          </figure>
        ) : null}
      </div>
    </div>
  );
}

export default function YogaUpload() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sessionScore, setSessionScore] = useState(0);
  const [timerSec, setTimerSec] = useState(0);
  const [matchedNames, setMatchedNames] = useState([]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [stageComplete, setStageComplete] = useState(null);
  const reportIdRef = useRef(null);
  const timerSecRef = useRef(0);
  const scoreRef = useRef(0);
  const matchedNamesRef = useRef([]);
  const timerIntervalRef = useRef(null);

  const pageTitle =
    themeData?.yoga_image_upload_page_title ||
    "Perform any yoga pose and upload a photo or video of yourself.";

  const titleColor = themeData?.text_color || "#1a2b6d";
  const btnStyle = {
    ...(themeData?.button_backgroundcolor && {
      backgroundColor: themeData.button_backgroundcolor,
    }),
    ...(themeData?.button_color && { color: themeData.button_color }),
  };

  const fromMobileAppInUrl = searchParams.get("fromMobileApp") === "true";
  const useFlutterBridge = useMemo(() => {
    if (isNativeAppWebView()) return true;
    if (fromMobileAppInUrl) return true;
    try {
      return sessionStorage.getItem(FROM_MOBILE_APP_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }, [fromMobileAppInUrl]);

  const useNativePickers = useMemo(() => {
    if (useFlutterBridge) return true;
    return isCoarseTouchDevice();
  }, [useFlutterBridge]);

  const maxUploadCount = useMemo(() => getUploadCount(themeData), [themeData]);
  const slotsLeft = Math.max(0, maxUploadCount - files.length);

  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current != null) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    if (timerIntervalRef.current != null) return;
    timerIntervalRef.current = window.setInterval(() => {
      timerSecRef.current += 1;
      setTimerSec(timerSecRef.current);
      saveYogaSession({
        score: scoreRef.current,
        timeSec: timerSecRef.current,
        matchedNames: matchedNamesRef.current,
      });
    }, 1000);
  }, []);

  const applyHudSession = useCallback((session) => {
    scoreRef.current = session.score;
    matchedNamesRef.current = session.matchedNames;
    timerSecRef.current = session.timeSec;
    setSessionScore(session.score);
    setTimerSec(session.timeSec);
    setMatchedNames(session.matchedNames);
    saveYogaSession(session);
  }, []);

  useEffect(() => {
    applyHudSession(readYogaSession());
    startTimer();

    return () => stopTimer();
  }, [applyHudSession, startTimer, stopTimer]);

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || "/login?&save=true"));
  }, [dispatch, user?.backButtonRedirect]);

  useEffect(() => {
    if (!user || !adminToken || !themeData || status !== "succeeded") return undefined;

    let cancelled = false;
    (async () => {
      try {
        const data = await fetchYogaGameStatus(adminToken, user);
        if (cancelled) return;

        const path = getYogaResumePath(data, themeData);
        if (path !== "/yoga-upload") {
          navigate(path, { replace: true });
          return;
        }

        const session = readYogaSession();
        const dbPoints = Number.parseInt(String(data.points ?? ""), 10) || 0;
        const dbTimeSec = parseTimeToSeconds(data.time);
        const merged = {
          ...session,
          score: session.score > 0 ? session.score : dbPoints,
          timeSec: session.timeSec > 0 ? session.timeSec : dbTimeSec,
        };
        if (merged.score !== session.score || merged.timeSec !== session.timeSec) {
          applyHudSession(merged);
        }
      } catch (e) {
        console.error("yoga resume route on upload:", e);
        if (!cancelled && !isUploadOpen(themeData)) {
          navigate("/yoga-gallery", { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, adminToken, themeData, status, navigate, applyHudSession]);

  const handleStageCompleteConfirm = useCallback(() => {
    if (!stageComplete?.nextPath) return;
    const nextPath = stageComplete.nextPath;
    setStageComplete(null);
    navigate(nextPath, { replace: true });
  }, [stageComplete, navigate]);

  const addFiles = useCallback(
    (incoming) => {
      const list = Array.from(incoming || []).filter(Boolean);
      if (!list.length) return;

      const valid = [];
      for (const next of list) {
        if (!isValidFile(next)) {
          Swal.fire("Invalid file", "Please choose JPG, PNG, MP4, or MOV (max 50 MB).", "warning");
          return;
        }
        if (next.size > MAX_BYTES) {
          Swal.fire("File too large", "Maximum file size is 50 MB.", "warning");
          return;
        }
        valid.push(next);
      }

      setFiles((prev) => {
        const room = Math.max(0, maxUploadCount - prev.length);
        if (room <= 0) {
          Swal.fire(
            "Upload limit reached",
            `You can upload at most ${maxUploadCount} file(s).`,
            "warning"
          );
          return prev;
        }
        const slice = valid.slice(0, room);
        const nextEntries = slice.map((f) => ({
          file: f,
          previewUrl: URL.createObjectURL(f),
        }));
        return [...prev, ...nextEntries];
      });
    },
    [maxUploadCount]
  );

  const removeFileAt = useCallback((index) => {
    setFiles((prev) => {
      const copy = [...prev];
      const [removed] = copy.splice(index, 1);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return copy;
    });
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      addFiles(e.target.files);
      e.target.value = "";
    },
    [addFiles]
  );

  useEffect(() => {
    const applyFlutterFile = async (detail) => {
      try {
        const next = await flutterPayloadToFile(detail);
        if (next) addFiles([next]);
      } catch (err) {
        console.error("yogaFilePicked", err);
        Swal.fire("Invalid file", "Could not read the selected file.", "warning");
      }
    };

    const onCustomEvent = (e) => {
      applyFlutterFile(e?.detail);
    };

    window.addEventListener(YOGA_FILE_PICKED_EVENT, onCustomEvent);
    window.receiveYogaMediaFile = applyFlutterFile;

    return () => {
      window.removeEventListener(YOGA_FILE_PICKED_EVENT, onCustomEvent);
      if (window.receiveYogaMediaFile === applyFlutterFile) {
        delete window.receiveYogaMediaFile;
      }
    };
  }, [addFiles]);

  const handleDrop = (e) => {
    if (useNativePickers) return;
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer?.files);
  };

  const handleSubmit = async () => {
    if (!files.length) {
      Swal.fire("No file", "Please choose a photo or video to upload.", "warning");
      return;
    }
    if (!user || !adminToken) {
      Swal.fire("Session expired", "Please log in again.", "error");
      return;
    }

    setUploading(true);
    stopTimer();
    const finalTimeSec = timerSecRef.current;
    const finalTimeStr = formatMmSs(finalTimeSec);

    try {
      const payload = await uploadYogaMediaViaPresign({
        files: files.map((entry) => entry.file),
        userId: user.userId,
        time: finalTimeStr,
        token: adminToken,
      });

      const finalPoints =
        payload?.points != null
          ? Number.parseInt(String(payload.points), 10) || scoreRef.current
          : scoreRef.current;

      scoreRef.current = finalPoints;
      setSessionScore(finalPoints);
      saveYogaSession({
        score: finalPoints,
        timeSec: finalTimeSec,
        matchedNames: matchedNamesRef.current,
      });

      const firstUploadKey = `yoga_first_upload_${user.userId}`;
      let firstUploadReported = false;
      try {
        firstUploadReported = localStorage.getItem(firstUploadKey) === "1";
      } catch {
        /* ignore */
      }
      if (!firstUploadReported) {
        addReportToServer(user, {
          points: 0,
          reportTime: "00:00",
          answers: ["first-upload"],
          standalone: true,
        });
        try {
          localStorage.setItem(firstUploadKey, "1");
        } catch {
          /* ignore */
        }
      }

      addReportToServer(user, {
        points: finalPoints,
        elapsedSeconds: finalTimeSec,
        answers: [...matchedNamesRef.current, "upload"],
        reportIdRef,
      });

      setStageComplete({
        text: "Your upload was successful. You've completed this stage! Next, view the gallery and see what everyone shared.",
        nextPath: "/yoga-gallery",
      });
    } catch (e) {
      console.error("uploadYogaMedia", e);
      const msg = String(e?.message || e);
      const title =
        msg.includes('S3') || msg.includes('CORS') ? 'Storage error' : 'Upload failed';
      await Swal.fire({ icon: "error", title, text: msg });
      setUploading(false);
      startTimer();
    }
  };

  if (status === "loading" || status === "idle") {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (status === "failed" || !themeData) {
    return (
      <div className="yu-page">
        <p style={{ textAlign: "center", color: titleColor }}>
          Could not load game data. Make sure the backend is running on port 5001.
          {themeError ? ` (${themeError})` : ""}
        </p>
        <button
          type="button"
          className="yu-submit"
          style={{ maxWidth: 200, margin: "16px auto", display: "block" }}
          onClick={() => dispatch(fetchThemeData({ themeId: null }))}
        >
          Retry
        </button>
      </div>
    );
  }

  const hasFiles = files.length > 0;
  const canAddMore = slotsLeft > 0;
  const sampleImgUrl = themeData?.yoga_sample_img
    ? s3ThemeAssetUrl(themeData.yoga_sample_img)
    : "";
  const sampleGifUrl = themeData?.yoga_sample_gif
    ? s3ThemeAssetUrl(themeData.yoga_sample_gif)
    : "";
  const hasSamples = Boolean(sampleImgUrl || sampleGifUrl);
  const rules = Array.isArray(themeData?.rules) ? themeData.rules : [];

  return (
    <div className="yu-page">
      <h1 className="yu-page-title" style={{ color: titleColor }}>
        {pageTitle}
      </h1>

      <YogaHud
        score={sessionScore}
        timeSec={timerSec}
        showProgress={false}
        showHowToPlay
        onHowToPlay={() => setRulesOpen(true)}
        inPanel={false}
      />

      <div className="yu-panel">
        <div className="yu-layout">
          <aside className="yu-info">
            <div className="yu-formats">
              {hasSamples ? (
                <YogaSamples
                  sampleImgUrl={sampleImgUrl}
                  sampleGifUrl={sampleGifUrl}
                  titleColor={titleColor}
                />
              ) : null}
              <h3>Supported Formats</h3>
              <ul>
                <li>
                  <span className="yu-format-icon">🖼</span> Image: JPG, PNG
                </li>
                <li>
                  <span className="yu-format-icon">▶</span> Video: MP4, MOV
                </li>
                <li>
                  <span className="yu-format-icon">📁</span> Max size: 50MB
                </li>
              </ul>
            </div>
          </aside>

          <section className="yu-upload-col">
            <p className="yu-upload-limit">
              {files.length} / {maxUploadCount} file{maxUploadCount === 1 ? "" : "s"} selected
            </p>

            {hasFiles ? (
              <div className="yu-preview-grid">
                {files.map((entry, index) => {
                  const pType = mediaPreviewType(entry.file);
                  return (
                    <div className="yu-preview-item" key={`${entry.file.name}-${index}`}>
                      {pType === "video" ? (
                        <video src={entry.previewUrl} className="yu-preview-thumb" controls />
                      ) : (
                        <img src={entry.previewUrl} alt="" className="yu-preview-thumb" />
                      )}
                      <p className="yu-file-name">{entry.file.name}</p>
                      <button
                        type="button"
                        className="yu-remove-file"
                        onClick={() => removeFileAt(index)}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {canAddMore ? (
              <div
                className={`yu-drop-zone${dragOver ? " yu-drop-zone--over" : ""}${
                  hasFiles ? " yu-drop-zone--has-file" : ""
                }${useNativePickers ? " yu-drop-zone--touch" : ""}`}
                onDragOver={
                  useNativePickers
                    ? undefined
                    : (e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }
                }
                onDragLeave={useNativePickers ? undefined : () => setDragOver(false)}
                onDrop={handleDrop}
              >
                {useNativePickers ? (
                  <>
                    <div className="yu-cloud-icon" aria-hidden>
                      ☁︎↑
                    </div>
                    <p className="yu-drop-text yu-drop-text--touch">
                      Choose a photo or video from your gallery
                    </p>
                    <div className="yu-mobile-pick-row">
                      <ChooseFileButton
                        useFlutterBridge={useFlutterBridge}
                        onInputChange={handleInputChange}
                        multiple={maxUploadCount > 1}
                        label={hasFiles ? "ADD FILE" : "CHOOSE FILE"}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="yu-cloud-icon" aria-hidden>
                      ☁︎↑
                    </div>
                    <p className="yu-drop-text">
                      Drag &amp; Drop your file{maxUploadCount > 1 ? "s" : ""} here
                      <span className="yu-drop-or">or</span>
                    </p>
                    <ChooseFileButton
                      useFlutterBridge={false}
                      onInputChange={handleInputChange}
                      multiple={maxUploadCount > 1}
                      label={hasFiles ? "ADD FILE" : "CHOOSE FILE"}
                    />
                  </>
                )}
              </div>
            ) : null}

            <button
              type="button"
              className="yu-submit"
              style={btnStyle.backgroundColor ? btnStyle : undefined}
              onClick={handleSubmit}
              disabled={!hasFiles || uploading}
            >
              {uploading ? "UPLOADING…" : "SUBMIT"}
            </button>
          </section>
        </div>

        <div className="yu-formats yu-formats--mobile">
          {hasSamples ? (
            <YogaSamples
              sampleImgUrl={sampleImgUrl}
              sampleGifUrl={sampleGifUrl}
              titleColor={titleColor}
            />
          ) : null}
          <h3>Supported Formats</h3>
          <ul>
            <li>
              <span className="yu-format-icon">🖼</span> Image: JPG, PNG
            </li>
            <li>
              <span className="yu-format-icon">▶</span> Video: MP4, MOV
            </li>
            <li>
              <span className="yu-format-icon">📁</span> Max size: 50MB
            </li>
          </ul>
        </div>
      </div>

      <YogaThemedModal
        open={rulesOpen}
        title="How to Play"
        titleId="yu-rules-title"
        themeData={themeData}
        confirmText="Got it"
        onConfirm={() => setRulesOpen(false)}
      >
        <ul>
          {rules.length > 0 ? (
            rules.map((rule, i) => <li key={i}>{rule}</li>)
          ) : (
            <>
              <li>Perform any yoga pose and upload a photo or video of yourself.</li>
              <li>Supported formats: JPG, PNG, MP4, MOV (max 50 MB).</li>
              <li>
                You can upload up to {maxUploadCount} file{maxUploadCount === 1 ? "" : "s"}.
              </li>
              <li>After submitting, you&apos;ll go to the gallery to see everyone&apos;s uploads.</li>
            </>
          )}
        </ul>
      </YogaThemedModal>

      <YogaThemedModal
        open={Boolean(stageComplete)}
        title="Congratulations!"
        titleId="yu-stage-complete-title"
        themeData={themeData}
        confirmText="Next"
        closeOnBackdrop={false}
        autoCloseMs={3000}
        onConfirm={handleStageCompleteConfirm}
      >
        <p>{stageComplete?.text}</p>
      </YogaThemedModal>
    </div>
  );
}
